import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import SEO from './SEO';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';


function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function daysLeft(iso) {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));
}

// ── Inline subscription editor ─────────────────────────────────────────────────
function SubscriptionEditor({ user, perms, onSaved }) {
  const [approved, setApproved] = useState(perms?.approved ?? false);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [confirm, setConfirm]   = useState(false);

  const isCustomActive = perms?.subscription_tier === 'custom' && perms?.subscription_status === 'active';

  const handleToggleApproved = async () => {
    const next = !approved;
    setSaving(true);
    try {
      await supabase.from('user_permissions').update({ approved: next }).eq('user_id', user.id);
      setApproved(next);
      onSaved(user.id, { approved: next });
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleActivateCustom = async () => {
    setConfirm(false);
    setSaving(true);
    try {
      const updates = { subscription_tier: 'custom', subscription_status: 'active', approved: true };
      if (perms) {
        await supabase.from('user_permissions').update(updates).eq('user_id', user.id);
      } else {
        await supabase.from('user_permissions').insert({ user_id: user.id, admin: false, ...updates });
      }
      setApproved(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved(user.id, updates);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="bg-bg-100 border-t border-bg-300">
      <td colSpan={8} className="px-6 py-5">
        <div className="flex flex-wrap items-start gap-6">

          {/* Profile data */}
          {perms?.data && (
            <div className="flex flex-wrap gap-6 text-sm">
              {perms.data.first_name && (
                <div>
                  <p className="text-text-300 text-xs uppercase tracking-widest mb-0.5">Name</p>
                  <p className="text-text-100">{perms.data.first_name} {perms.data.last_name || ''}</p>
                </div>
              )}
              {perms.data.firm_name && (
                <div>
                  <p className="text-text-300 text-xs uppercase tracking-widest mb-0.5">Firm</p>
                  <p className="text-text-100">{perms.data.firm_name}</p>
                </div>
              )}
              {perms.data.phone && (
                <div>
                  <p className="text-text-300 text-xs uppercase tracking-widest mb-0.5">Phone</p>
                  <p className="text-text-100">{perms.data.phone}</p>
                </div>
              )}
              {perms.data.role && (
                <div>
                  <p className="text-text-300 text-xs uppercase tracking-widest mb-0.5">Role</p>
                  <p className="text-text-100">{perms.data.role}</p>
                </div>
              )}
              {perms.data.aum_range && (
                <div>
                  <p className="text-text-300 text-xs uppercase tracking-widest mb-0.5">AUM</p>
                  <p className="text-text-100">{perms.data.aum_range}</p>
                </div>
              )}
              {perms.data.referral_source && (
                <div>
                  <p className="text-text-300 text-xs uppercase tracking-widest mb-0.5">Source</p>
                  <p className="text-text-100">{perms.data.referral_source}</p>
                </div>
              )}
              {perms.data.signup_source && (
                <div className="max-w-md">
                  <p className="text-text-300 text-xs uppercase tracking-widest mb-0.5">Referrer</p>
                  <p className="text-text-100 break-all">{perms.data.signup_source.referrer || 'Direct'}</p>
                  {perms.data.signup_source.landing_page && (
                    <p className="text-text-300 text-xs break-all">Landed on {perms.data.signup_source.landing_page}</p>
                  )}
                  {Object.keys(perms.data.signup_source.query_params || {}).length > 0 && (
                    <p className="text-text-300 text-xs break-all">
                      {Object.entries(perms.data.signup_source.query_params).map(([k, v]) => `${k}=${v}`).join(' · ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="h-auto w-px bg-bg-300 self-stretch hidden sm:block" />

          {/* Current plan info */}
          <div className="text-sm space-y-1">
            <p className="text-text-300 text-xs uppercase tracking-widest mb-1">Current plan</p>
            <p className="text-text-100 font-medium">{perms?.subscription_tier || 'none'} · {perms?.subscription_status || 'inactive'}</p>
            {perms?.trial_ends_at && (
              <p className="text-text-300 text-xs">
                Trial ends {formatDate(perms.trial_ends_at)}
                {daysLeft(perms.trial_ends_at) !== null && (
                  <span className={`ml-1 ${daysLeft(perms.trial_ends_at) <= 3 ? 'text-red-400' : ''}`}>
                    ({daysLeft(perms.trial_ends_at)}d left)
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="h-auto w-px bg-bg-300 self-stretch hidden sm:block" />

          {/* Access toggle */}
          <div>
            <p className="text-text-300 text-xs uppercase tracking-widest mb-2">Access</p>
            <button
              onClick={handleToggleApproved}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 ${approved ? 'bg-signal-positive' : 'bg-bg-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${approved ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <p className={`text-xs mt-1 ${approved ? 'text-signal-positive' : 'text-text-300'}`}>{approved ? 'Active' : 'Inactive'}</p>
          </div>

          <div className="h-auto w-px bg-bg-300 self-stretch hidden sm:block" />

          {/* Activate custom */}
          <div className="flex flex-col gap-2">
            {saved && (
              <p className="text-xs text-signal-positive font-medium">✓ Custom subscription activated</p>
            )}
            {!confirm ? (
              <button
                onClick={() => setConfirm(true)}
                disabled={saving || isCustomActive}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-purple-500 text-purple-300 hover:bg-purple-900 hover:bg-opacity-30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isCustomActive ? '✦ Custom subscription active' : '✦ Activate custom subscription'}
              </button>
            ) : (
              <div className="rounded-lg border border-purple-500 border-opacity-50 bg-purple-900 bg-opacity-15 p-3 space-y-2 max-w-xs">
                <p className="text-xs text-purple-200 font-medium">Activate custom subscription for <span className="text-white">{user.email}</span>?</p>
                <p className="text-xs text-text-300">This will set their account to live with a custom plan and grant full access.</p>
                <div className="flex gap-2">
                  <button
                    onClick={handleActivateCustom}
                    disabled={saving}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-600 hover:bg-purple-500 text-white transition-all disabled:opacity-60"
                  >
                    {saving ? 'Activating…' : 'Confirm & activate'}
                  </button>
                  <button
                    onClick={() => setConfirm(false)}
                    className="px-3 py-1.5 rounded-lg text-xs text-text-300 hover:text-text-100 border border-bg-300 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </td>
    </tr>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [permsMap, setPermsMap] = useState({}); // user_id → user_permissions row
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) { navigate('/models'); return; }
      try {
        const { data, error } = await supabase
          .from('user_permissions').select('admin').eq('user_id', user.id).single();
        if (error || !data?.admin) { navigate('/models'); return; }
        fetchUsers();
      } catch { navigate('/models'); }
    };
    checkAdmin();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersRes, permsRes] = await Promise.all([
        supabase.rpc('get_users_admin'),
        supabase.from('user_permissions').select('*'),
      ]);

      if (usersRes.error) throw new Error(usersRes.error.message);

      // Build a map of user_id → permissions row
      const map = {};
      (permsRes.data || []).forEach((p) => { map[p.user_id] = p; });

      setUsers(usersRes.data || []);
      setPermsMap(map);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = (userId, updates) => {
    setPermsMap((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || { user_id: userId }), ...updates },
    }));
    // Also sync approved into the users array so the badge updates inline
    setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, approved: updates.approved } : u));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => sortField === field
    ? <span className="text-primary-100">{sortDirection === 'asc' ? '↑' : '↓'}</span>
    : null;

  const filteredUsers = users
    .filter((u) => u.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (sortField === 'created_at' || sortField === 'last_sign_in_at') {
        av = new Date(av).getTime(); bv = new Date(bv).getTime();
      }
      if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      return av < bv ? (sortDirection === 'asc' ? -1 : 1) : av > bv ? (sortDirection === 'asc' ? 1 : -1) : 0;
    });

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-bg-100">
      <div className="w-6 h-6 border-2 border-primary-100 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-screen bg-bg-100">
      <div className="text-center space-y-4">
        <p className="text-red-400">Error: {error}</p>
        <button onClick={() => navigate('/models')} className="px-4 py-2 bg-bg-200 text-text-100 rounded hover:bg-bg-300">Back to App</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bg-100 p-6">
      <SEO title="Admin" />
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text-100">Admin Panel</h1>
            <p className="text-text-300 text-sm mt-0.5">{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={() => navigate('/models')} className="px-4 py-2 bg-bg-200 text-text-100 rounded-lg hover:bg-bg-300 text-sm">
            ← Back to App
          </button>
        </div>

        <div className="bg-bg-200 rounded-xl border border-bg-300 overflow-hidden">
          <div className="p-4 border-b border-bg-300">
            <input
              type="text"
              placeholder="Search by email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-bg-100 text-text-100 rounded-lg border border-bg-300 focus:outline-none focus:ring-2 focus:ring-primary-100 text-sm"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-bg-300">
                <tr>
                  {[
                    { label: 'Email',      field: 'email' },
                    { label: 'Signed up',  field: 'created_at' },
                    { label: 'Last login', field: 'last_sign_in_at' },
                    { label: 'Sessions',   field: 'session_count', center: true },
                    { label: 'Plan',       field: 'subscription_tier', center: true },
                    { label: 'Admin',      field: 'admin', center: true },
                    { label: 'Approved',   field: 'approved', center: true },
                    { label: '',           field: null },
                  ].map(({ label, field, center }) => (
                    <th
                      key={label}
                      onClick={field ? () => handleSort(field) : undefined}
                      className={`px-4 py-3 text-xs font-medium text-text-200 uppercase tracking-wider ${center ? 'text-center' : 'text-left'} ${field ? 'cursor-pointer hover:text-text-100 select-none' : ''}`}
                    >
                      <span className="flex items-center gap-1 justify-inherit">
                        {label} {field && <SortIcon field={field} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-bg-300">
                {filteredUsers.map((u) => {
                  const perms = permsMap[u.id];
                  const isOpen = expandedId === u.id;
                  const tier = perms?.subscription_tier;

                  return (
                    <React.Fragment key={u.id}>
                      <tr className={`transition-colors ${isOpen ? 'bg-bg-300' : 'hover:bg-bg-300'}`}>
                        {/* Email */}
                        <td className="px-4 py-3">
                          <p className="text-sm text-text-100">{u.email}</p>
                          {perms?.data?.firm_name && (
                            <p className="text-xs text-text-300">{perms.data.firm_name}</p>
                          )}
                        </td>
                        {/* Signed up */}
                        <td className="px-4 py-3 text-sm text-text-200 whitespace-nowrap">{formatDate(u.created_at)}</td>
                        {/* Last login */}
                        <td className="px-4 py-3 text-sm text-text-200 whitespace-nowrap">{formatDate(u.last_sign_in_at)}</td>
                        {/* Sessions */}
                        <td className="px-4 py-3 text-center text-sm text-text-100">{u.session_count || 0}</td>
                        {/* Plan */}
                        <td className="px-4 py-3 text-center">
                          {tier && tier !== 'none' ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              tier === 'trial'     ? 'bg-accent-100 bg-opacity-15 text-accent-100' :
                              tier === 'annual'    ? 'bg-signal-positive bg-opacity-15 text-signal-positive' :
                              tier === 'quarterly' ? 'bg-primary-100 bg-opacity-15 text-primary-100' :
                              tier === 'custom'    ? 'bg-purple-900 bg-opacity-40 text-purple-300' :
                              'bg-bg-300 text-text-300'
                            }`}>
                              {tier}
                              {tier === 'trial' && perms?.trial_ends_at && (
                                <span className="ml-1 opacity-70">· {daysLeft(perms.trial_ends_at)}d</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-text-300 text-xs">—</span>
                          )}
                        </td>
                        {/* Admin */}
                        <td className="px-4 py-3 text-center">
                          {u.admin
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-900 text-purple-200">Admin</span>
                            : <span className="text-text-300">—</span>}
                        </td>
                        {/* Approved */}
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block w-2 h-2 rounded-full ${u.approved ? 'bg-signal-positive' : 'bg-bg-300'}`} />
                          <span className={`ml-1.5 text-xs ${u.approved ? 'text-signal-positive' : 'text-text-300'}`}>
                            {u.approved ? 'Yes' : 'No'}
                          </span>
                        </td>
                        {/* Expand */}
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setExpandedId(isOpen ? null : u.id)}
                            className="p-1 rounded hover:bg-bg-200 text-text-300 hover:text-text-100 transition-colors"
                            title="Edit subscription"
                          >
                            {isOpen
                              ? <ChevronUpIcon className="w-4 h-4" />
                              : <ChevronDownIcon className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded editor row */}
                      {isOpen && (
                        <SubscriptionEditor
                          user={u}
                          perms={perms}
                          onSaved={handleSaved}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-text-300">No users found</div>
          )}
        </div>
      </div>
    </div>
  );
}
